import {
  menu,
  type MenuHandler,
  popFilterableSimpleMenu,
  type PopupTarget,
} from '@blocksuite/affine-components/context-menu';
import { HEIC_EXTS, isHeicMime } from '@blocksuite/affine-shared/utils';
import {
  AttachmentIcon,
  CopyIcon,
  DeleteIcon,
  DownloadIcon,
  ExpandFullIcon,
  MoveLeftIcon,
  MoveRightIcon,
  ResetIcon,
} from '@blocksuite/icons/lit';
import { html } from 'lit';

import { dataViewI18nT } from '../../../core/i18n.js';
import { rowMenuExtensions } from '../row-menu-extensions.js';
import { TableViewRowSelection } from '../selection';
import type { TableSelectionController } from './controller/selection.js';
import type { TableViewUILogic } from './table-view-ui-logic.js';

/**
 * sl-luoo: Count HEIC files across all attachment/file columns for the given rows.
 * Returns the total count and a list of {rowId, propertyId} pairs for dispatch.
 */
function countHeicFilesInRows(
  view: TableViewUILogic['view'],
  rowIds: string[]
): { count: number; cells: { rowId: string; propertyId: string }[] } {
  const fileCells: { rowId: string; propertyId: string }[] = [];
  let count = 0;

  const properties = view.getBoundedPropertiesWindow().properties;
  for (const prop of properties) {
    if (prop.type$.value !== 'attachment') continue;
    for (const rowId of rowIds) {
      const rawValue = prop.cellGetOrCreate(rowId).value$.value as
        | Record<string, { name?: string; mime?: string }>
        | undefined;
      if (!rawValue) continue;
      let hasHeic = false;
      for (const entry of Object.values(rawValue)) {
        if (entry.mime && isHeicMime(entry.mime)) {
          hasHeic = true;
          count++;
          continue;
        }
        const ext = entry.name?.split('.').pop()?.toLowerCase();
        if (ext && HEIC_EXTS.has(ext)) {
          hasHeic = true;
          count++;
        }
      }
      if (hasHeic) {
        fileCells.push({ rowId, propertyId: prop.id });
      }
    }
  }
  return { count, cells: fileCells };
}

/**
 * Dispatch a window-level CustomEvent for file-cell operations.
 * Each mounted React file-cell checks if it's a target via rowId:propertyId matching.
 * This avoids DOM querySelector issues with virtual tables or off-screen cells.
 */
function dispatchFileCellEvent(
  eventName: string,
  cells: { rowId: string; propertyId: string }[]
): void {
  window.dispatchEvent(new CustomEvent(eventName, { detail: { cells } }));
}

/**
 * sl-luoo: Count total attachment files across all attachment columns for the given rows.
 * Returns {rowId, propertyId} pairs for all cells that have any attachments.
 */
function countAttachmentsInRows(
  view: TableViewUILogic['view'],
  rowIds: string[]
): { count: number; cells: { rowId: string; propertyId: string }[] } {
  const fileCells: { rowId: string; propertyId: string }[] = [];
  let count = 0;
  const properties = view.getBoundedPropertiesWindow().properties;
  for (const prop of properties) {
    if (prop.type$.value !== 'attachment') continue;
    for (const rowId of rowIds) {
      const rawValue = prop.cellGetOrCreate(rowId).value$.value as
        | Record<string, unknown>
        | undefined;
      if (!rawValue) continue;
      const n = Object.keys(rawValue).length;
      if (n > 0) {
        count += n;
        fileCells.push({ rowId, propertyId: prop.id });
      }
    }
  }
  return { count, cells: fileCells };
}

/**
 * Build the "Attachments ▸" submenu config. Only shown when the selected rows
 * contain at least one attachment file. Contains:
 * - Convert HEIC → JPEG (only when HEIC files present)
 * - Download All
 */
function attachmentsSubMenu(
  heic: ReturnType<typeof countHeicFilesInRows>,
  attachments: ReturnType<typeof countAttachmentsInRows>
) {
  if (attachments.count === 0) return [];
  return [
    menu.group({
      name: '',
      items: [
        menu.subMenu({
          name: dataViewI18nT('row.attachments'),
          prefix: AttachmentIcon(),
          options: {
            items: [
              // Convert HEIC (only when HEIC files present)
              ...(heic.count > 0
                ? [
                    menu.action({
                      name:
                        heic.count === 1
                          ? 'Convert HEIC → JPEG'
                          : `Convert ${heic.count} HEIC → JPEG`,
                      prefix: ResetIcon(),
                      select: () =>
                        dispatchFileCellEvent('sl-convert-heic', heic.cells),
                    }),
                  ]
                : []),
              // Download All
              menu.action({
                name: dataViewI18nT('row.attachmentsDownloadAll', {
                  count: attachments.count,
                }),
                prefix: DownloadIcon(),
                select: () =>
                  dispatchFileCellEvent('sl-download-all', attachments.cells),
              }),
            ],
          },
        }),
      ],
    }),
  ];
}

export const openDetail = (
  tableViewLogic: TableViewUILogic,
  rowId: string,
  selection: TableSelectionController
) => {
  const old = selection.selection;
  selection.selection = undefined;
  tableViewLogic.root.openDetailPanel({
    view: selection.logic.view,
    rowId: rowId,
    onClose: () => {
      selection.selection = old;
    },
  });
};

// Track the active row menu so we can close it before opening a new one.
// Without this, rapid right-clicks stack multiple popups on top of each other.
// The global mousedown listener ensures the menu closes when clicking anywhere
// outside — the default BlockSuite popup system only captures clicks within the
// editor-host container, so clicks in React panels or outside the editor won't
// dismiss the menu without this.
let activeRowMenu: MenuHandler | undefined;

const dismissOnOutsideClick = (e: MouseEvent) => {
  if (!activeRowMenu) return;
  // Sub-menus are siblings (not children) of the main menu element,
  // so we check if the click target is inside ANY affine-menu element.
  const target = e.target as HTMLElement;
  if (
    target.closest?.('affine-menu') ||
    target.closest?.('affine-menu-sub-menu')
  ) {
    return;
  }
  activeRowMenu.close();
  activeRowMenu = undefined;
};
// Single global listener — only fires when a menu is open.
if (typeof document !== 'undefined') {
  document.addEventListener('mousedown', dismissOnOutsideClick, true);
}

export const popRowMenu = (
  tableViewLogic: TableViewUILogic,
  ele: PopupTarget,
  selectionController: TableSelectionController
) => {
  // Close any previously open row menu
  activeRowMenu?.close();
  activeRowMenu = undefined;

  const selection = selectionController.selection;
  if (!TableViewRowSelection.is(selection)) {
    return;
  }

  // sl-9qm.21.3: System-managed enforcement for row menus
  const dataSource = tableViewLogic.root.config.dataSource;
  const isSystemManaged = dataSource.systemManaged$?.value ?? false;
  const canDelete =
    !isSystemManaged || (dataSource.allowDelete$?.value ?? false);

  // sl-luoo: Check for attachments and HEIC files across all selected rows
  const allRowIds = TableViewRowSelection.rowsIds(selection);
  const heic = countHeicFilesInRows(selectionController.view, allRowIds);
  const attachments = countAttachmentsInRows(
    selectionController.view,
    allRowIds
  );

  if (selection.rows.length > 1) {
    const rows = allRowIds;
    const menuItems = [
      menu.group({
        name: '',
        items: [
          menu.action({
            name: dataViewI18nT('row.copy'),
            prefix: html` <div
              style="transform: rotate(90deg);display:flex;align-items:center;"
            >
              ${CopyIcon()}
            </div>`,
            select: () => {
              selectionController.logic.clipboardController.copy();
            },
          }),
        ],
      }),
      // sl-luoo: Attachments submenu (Convert HEIC, Download All)
      ...attachmentsSubMenu(heic, attachments),
      // sl-9qm.21.3: Hide delete when system-managed and allowDelete=false
      ...(canDelete
        ? [
            menu.group({
              name: '',
              items: [
                menu.action({
                  name: dataViewI18nT('row.deleteMany'),
                  class: {
                    'delete-item': true,
                  },
                  prefix: DeleteIcon(),
                  select: () => {
                    if (rows.length > 1) {
                      const win = window as Window & {
                        __streamlainHooks?: {
                          confirmDeleteRows?: (
                            count: number
                          ) => Promise<boolean>;
                        };
                      };
                      const confirm = win.__streamlainHooks?.confirmDeleteRows;
                      if (confirm) {
                        void confirm(rows.length).then(confirmed => {
                          if (confirmed) {
                            selectionController.view.rowsDelete(rows);
                            selectionController.logic.ui$.value?.requestUpdate();
                          }
                        });
                        return;
                      }
                    }
                    selectionController.view.rowsDelete(rows);
                    selectionController.logic.ui$.value?.requestUpdate();
                  },
                }),
              ],
            }),
          ]
        : []),
    ];
    activeRowMenu = popFilterableSimpleMenu(ele, menuItems);
    return;
  }
  const row = selection.rows[0];
  if (!row) return;
  activeRowMenu = popFilterableSimpleMenu(ele, [
    menu.action({
      name: dataViewI18nT('row.expand'),
      prefix: ExpandFullIcon(),
      select: () => {
        openDetail(tableViewLogic, row.id, selectionController);
      },
    }),
    // sl-9qm.21.3: Hide insert actions when system-managed
    ...(isSystemManaged
      ? []
      : [
          menu.group({
            name: '',
            items: [
              menu.action({
                name: dataViewI18nT('row.insertBefore'),
                prefix: html` <div
                  style="transform: rotate(90deg);display:flex;align-items:center;"
                >
                  ${MoveLeftIcon()}
                </div>`,
                select: () => {
                  selectionController.insertRowBefore(row.groupKey, row.id);
                },
              }),
              menu.action({
                name: dataViewI18nT('row.insertAfter'),
                prefix: html` <div
                  style="transform: rotate(90deg);display:flex;align-items:center;"
                >
                  ${MoveRightIcon()}
                </div>`,
                select: () => {
                  selectionController.insertRowAfter(row.groupKey, row.id);
                },
              }),
            ],
          }),
        ]),
    // Host items for this row (StreamLain: the runs it started).
    ...rowMenuExtensions(dataSource, row.id),
    // sl-luoo: Attachments submenu (Convert HEIC, Download All)
    ...attachmentsSubMenu(heic, attachments),
    // sl-9qm.21.3: Hide delete when system-managed and allowDelete=false
    ...(canDelete
      ? [
          menu.group({
            items: [
              menu.action({
                name: dataViewI18nT('row.delete'),
                class: { 'delete-item': true },
                prefix: DeleteIcon(),
                select: () => {
                  selectionController.deleteRow(row.id);
                },
              }),
            ],
          }),
        ]
      : []),
  ]);
};
